// 录音：麦克风 → 16 kHz、16 位、单声道 PCM，每 100 毫秒一段（阿里云实时识别要的格式），顺带算出这一段有多响。
// 浏览器的采样率多半是 48k / 44.1k：几个采样取平均降到 16k（也顺带滤掉高频）。处理放在 AudioWorklet 里，不卡页面；
// 老浏览器没有 AudioWorklet 就用 ScriptProcessor。

/** 降采样：一段段喂 Float32，攒够 100 毫秒吐一段 Int16。也放进 AudioWorklet 里用（toString 出源码），所以不引用外面的东西。 */
export class Downsampler {
  ratio: number
  pos: number
  acc: number
  n: number
  out: Int16Array
  len: number
  sum: number
  constructor(rate: number) {
    this.ratio = rate / 16000
    this.pos = 0
    this.acc = 0
    this.n = 0
    this.out = new Int16Array(1600)
    this.len = 0
    this.sum = 0
  }
  /** 喂一段；攒满的段交给 emit（pcm, 均方根响度 0–1）。 */
  push(ch: Float32Array, emit: (pcm: ArrayBuffer, rms: number) => void) {
    for (let i = 0; i < ch.length; i++) {
      this.acc += ch[i]
      this.n++
      this.pos += 1
      if (this.pos >= this.ratio) {
        this.pos -= this.ratio
        const v = Math.max(-1, Math.min(1, this.acc / this.n))
        this.acc = 0
        this.n = 0
        this.out[this.len++] = v < 0 ? v * 0x8000 : v * 0x7fff
        this.sum += v * v
        if (this.len === this.out.length) this.flush(emit)
      }
    }
  }
  /** 把没攒满的也吐出来（说完的时候）。 */
  flush(emit: (pcm: ArrayBuffer, rms: number) => void) {
    if (!this.len) return
    const pcm = this.out.slice(0, this.len)
    const rms = Math.sqrt(this.sum / this.len)
    this.len = 0
    this.sum = 0
    emit(pcm.buffer as ArrayBuffer, rms)
  }
}

const WORKLET = (D: string) => `
const Downsampler = ${D};
class LingzhiPcm16k extends AudioWorkletProcessor {
  constructor() {
    super();
    this.d = new Downsampler(sampleRate);
    this.emit = (pcm, rms) => this.port.postMessage({ pcm, rms }, [pcm]);
    this.port.onmessage = (e) => { if (e.data === 'flush') { this.d.flush(this.emit); this.port.postMessage({ flushed: true }); } };
  }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (ch) this.d.push(ch, this.emit);
    return true;
  }
}
registerProcessor('lingzhi-pcm16k', LingzhiPcm16k);
`

export class CaptureError extends Error {}

export interface Capture {
  /** 吐出最后一段、关掉麦克风（浏览器上的录音标记随之消失）。 */
  stop(): Promise<void>
}

/** 浏览器能不能录音；不能的话为什么。 */
export function captureProblem(): string | null {
  if (typeof window === 'undefined') return '这个环境不能录音'
  if (!window.isSecureContext) return '要在 https 的网址下才能用麦克风'
  if (!navigator.mediaDevices?.getUserMedia) return '这个浏览器不能录音'
  if (!(window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext)) return '这个浏览器不能录音'
  return null
}

/** 点按钮的那一下就建好 AudioContext（iPhone 要在点击里建，不然没声音）。 */
export function newAudioContext(): AudioContext {
  const Ctx = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext!
  return new Ctx()
}

/**
 * 载入降采样的 AudioWorklet。有的浏览器（2026-10-12 线上验收用的内嵌 Chromium）addModule 一直不返回，
 * 1.5 秒还没好就当不支持，改用 ScriptProcessor（正常的浏览器几十毫秒就载好了）。
 */
async function loadWorklet(ctx: AudioContext): Promise<boolean> {
  const url = URL.createObjectURL(new Blob([WORKLET(Downsampler.toString())], { type: 'text/javascript' }))
  try {
    return await Promise.race([ctx.audioWorklet.addModule(url).then(() => true, () => false), new Promise<boolean>((r) => setTimeout(() => r(false), 1500))])
  } finally {
    URL.revokeObjectURL(url)
  }
}

export async function startCapture(ctx: AudioContext, onChunk: (pcm: ArrayBuffer, rms: number) => void): Promise<Capture> {
  let stream: MediaStream
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true } })
  } catch (e) {
    const name = (e as { name?: string }).name
    if (name === 'NotAllowedError' || name === 'SecurityError') throw new CaptureError('没有麦克风权限：在浏览器的网站设置里允许使用麦克风')
    if (name === 'NotFoundError' || name === 'OverconstrainedError') throw new CaptureError('没有找到麦克风')
    if (name === 'NotReadableError') throw new CaptureError('麦克风被别的程序占着')
    throw new CaptureError('打不开麦克风')
  }
  if (ctx.state === 'suspended') await ctx.resume().catch(() => undefined)
  const source = ctx.createMediaStreamSource(stream)
  // 接到一个静音的出口上，浏览器才会一直处理（不会把你的声音放出来）
  const mute = ctx.createGain()
  mute.gain.value = 0
  mute.connect(ctx.destination)
  const release = () => {
    stream.getTracks().forEach((t) => t.stop())
    source.disconnect()
    mute.disconnect()
    void ctx.close().catch(() => undefined)
  }

  if (ctx.audioWorklet && typeof AudioWorkletNode !== 'undefined' && (await loadWorklet(ctx))) {
    const node = new AudioWorkletNode(ctx, 'lingzhi-pcm16k', { numberOfInputs: 1, numberOfOutputs: 1, channelCount: 1, channelCountMode: 'explicit' })
    let flushed: (() => void) | null = null
    node.port.onmessage = (e: MessageEvent<{ pcm?: ArrayBuffer; rms?: number; flushed?: boolean }>) => {
      if (e.data.pcm) onChunk(e.data.pcm, e.data.rms ?? 0)
      if (e.data.flushed) flushed?.()
    }
    source.connect(node)
    node.connect(mute)
    return {
      async stop() {
        await new Promise<void>((r) => {
          flushed = r
          node.port.postMessage('flush')
          setTimeout(r, 300)
        })
        node.port.onmessage = null
        node.disconnect()
        release()
      },
    }
  }

  // 老浏览器，或者 AudioWorklet 载不进来
  const d = new Downsampler(ctx.sampleRate)
  const proc = ctx.createScriptProcessor(4096, 1, 1)
  proc.onaudioprocess = (e) => d.push(e.inputBuffer.getChannelData(0), onChunk)
  source.connect(proc)
  proc.connect(mute)
  return {
    async stop() {
      d.flush(onChunk)
      proc.onaudioprocess = null
      proc.disconnect()
      release()
    },
  }
}
